import React from "react";
import { useLocation } from "react-router-dom";

const DEFAULT_TITLE = "Book One or Multiple Cars for a Trip on Wayroot";
const DEFAULT_DESCRIPTION =
  "Wayroot lets users book one or multiple cars in a single plan for trips lasting hours or days, with local and outstation options for one-way and round trips at competitive rates.";
const SITE_URL = "https://wayroot.com";
const DEFAULT_IMAGE = `${SITE_URL}/web-app-manifest-512x512.png`;

const HOME_SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Wayroot Technologies Private Limited",
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    email: "support@wayroot.com",
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Wayroot",
    url: SITE_URL,
    description: DEFAULT_DESCRIPTION,
  },
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Wayroot",
    applicationCategory: "TravelApplication",
    operatingSystem: "Web",
    url: SITE_URL,
    description:
      "Wayroot is a reservations platform designed for events, corporates, and group travel with live availability, mixed fleets, and one seamless checkout.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    publisher: {
      "@type": "Organization",
      name: "Wayroot Technologies Private Limited",
    },
  },
];

const PAGE_META = {
  "/": {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    schema: HOME_SCHEMA,
  },
  "/discover": {
    title: "Discover Cars | Wayroot",
    description:
      "Search available cars on Wayroot, choose the vehicle types you need, and build your trip with clear pricing and a simple checkout.",
  },
  "/about-wayroot": {
    title: "About Wayroot",
    description:
      "Learn how Wayroot helps users book cars for flexible trip durations, reserve one or multiple vehicles in one plan, and arrange local or outstation travel with clear pricing.",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Wayroot",
    description:
      "Read the Wayroot privacy policy for how personal information is collected, used, stored, and protected.",
  },
  "/delete-account": {
    title: "Delete Account | Wayroot",
    description:
      "Learn how to request deletion of your Wayroot account and associated personal information.",
  },
};

function ensureMeta(attr, key, value) {
  if (!value) return;
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", value);
}

function ensureLink(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

function clearWayrootSchema() {
  const existing = document.head.querySelectorAll('script[data-wayroot-schema="true"]');
  existing.forEach((node) => node.remove());
}

function addSchema(entries) {
  entries.forEach((entry, index) => {
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.setAttribute("data-wayroot-schema", "true");
    tag.id = `wayroot-schema-${index}`;
    tag.textContent = JSON.stringify(entry);
    document.head.appendChild(tag);
  });
}

export default function RouteSeo() {
  const location = useLocation();

  React.useEffect(() => {
    const page = PAGE_META[location.pathname] || {};
    const title = page.title || DEFAULT_TITLE;
    const description = page.description || DEFAULT_DESCRIPTION;
    const canonical = `${SITE_URL}${location.pathname === "/" ? "/" : location.pathname}`;

    document.title = title;
    ensureMeta("name", "description", description);
    ensureMeta("name", "application-name", "Wayroot");
    ensureMeta("property", "og:site_name", "Wayroot");
    ensureMeta("property", "og:type", "website");
    ensureMeta("property", "og:title", title);
    ensureMeta("property", "og:description", description);
    ensureMeta("property", "og:url", canonical);
    ensureMeta("property", "og:image", DEFAULT_IMAGE);
    ensureMeta("name", "twitter:card", "summary_large_image");
    ensureMeta("name", "twitter:title", title);
    ensureMeta("name", "twitter:description", description);
    ensureMeta("name", "twitter:image", DEFAULT_IMAGE);
    ensureLink("canonical", canonical);

    clearWayrootSchema();
    addSchema(page.schema || []);
  }, [location.pathname]);

  return null;
}
